import React from "react";
import {
  getValueAnalysis,
  saveValueClassifications,
} from "../../services/valueAnalysisService";

// VA / BVA / NVA (FBPM Ch.6).
const CLASSES = [
  { key: "VA", label: "VA", full: "Value-adding", color: "#2e8b63", bg: "#e6f3ec", hint: "The customer values / would pay for this." },
  { key: "BVA", label: "BVA", full: "Business value-adding", color: "#a96f18", bg: "#f6eede", hint: "Necessary for the business (compliance, control) but not customer-valued." },
  { key: "NVA", label: "NVA", full: "Non-value-adding", color: "#b5341f", bg: "#fbeae7", hint: "Neither — a candidate for elimination." },
];
const classOf = (k) => CLASSES.find((c) => c.key === k) || null;

const inputStyle = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  fontSize: 12.5,
  boxSizing: "border-box",
  background: "#fff",
};

function Chip({ k }) {
  const c = classOf(k);
  if (!c) return <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>unclassified</span>;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: c.color, background: c.bg, border: `1px solid ${c.color}33`, borderRadius: 999, padding: "2px 8px" }} title={c.full}>
      {c.label}
    </span>
  );
}

/**
 * Value-Added analysis panel.
 * - readOnly=true  → View: classification chips + rationale, no editing.
 * - readOnly=false → Edit: pick VA/BVA/NVA per step, add rationale / BVA goal.
 */
export default function ValueAddedPanel({ processId, isActive, readOnly = false }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({});
  const originalRef = React.useRef({});

  const load = React.useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getValueAnalysis(processId);
      setData(res);
      const seed = {};
      (res.tasks || []).forEach((t) => {
        seed[t.process_task_id] = {
          value_classification: t.value_classification || "",
          value_rationale: t.value_rationale || "",
          bva_business_goal: t.bva_business_goal || "",
        };
      });
      setForm(seed);
      originalRef.current = JSON.parse(JSON.stringify(seed));
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load value analysis");
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    const shouldLoad = isActive === undefined ? true : isActive;
    if (shouldLoad && processId && !loaded && !loading) load();
  }, [isActive, processId, loaded, loading, load]);

  const setField = (ptId, field, value) =>
    setForm((prev) => ({ ...prev, [ptId]: { ...(prev[ptId] || {}), [field]: value } }));

  const pick = (ptId, k) =>
    setField(ptId, "value_classification", form[ptId]?.value_classification === k ? "" : k);

  const liveRollup = React.useMemo(() => {
    const r = { VA: 0, BVA: 0, NVA: 0, UNC: 0 };
    Object.values(form).forEach((v) => {
      const c = v?.value_classification;
      if (c === "VA") r.VA++;
      else if (c === "BVA") r.BVA++;
      else if (c === "NVA") r.NVA++;
      else r.UNC++;
    });
    return r;
  }, [form]);

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg("");
    setError("");
    try {
      const orig = originalRef.current;
      const items = Object.entries(form)
        .filter(([ptId, v]) => {
          const o = orig[ptId] || {};
          return (
            (v.value_classification || "") !== (o.value_classification || "") ||
            (v.value_rationale || "") !== (o.value_rationale || "") ||
            (v.bva_business_goal || "") !== (o.bva_business_goal || "")
          );
        })
        .map(([ptId, v]) => ({
          process_task_id: Number(ptId),
          value_classification: v.value_classification || null,
          value_rationale: v.value_rationale || undefined,
          bva_business_goal:
            v.value_classification === "BVA" ? v.bva_business_goal || undefined : undefined,
        }));
      if (items.length === 0) {
        setSavedMsg("No changes to save.");
      } else {
        await saveValueClassifications(processId, items);
        setSavedMsg(`Saved ${items.length} step${items.length === 1 ? "" : "s"}.`);
        setLoaded(false);
        await load();
      }
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Value-Added Analysis</h3>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          Classify each step: value-adding, business value-adding, or non-value-adding
        </span>
      </div>

      {loading && <p style={{ color: "#6b7280" }}>Loading…</p>}
      {error && (
        <div style={{ margin: "10px 0", padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Rollup */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "6px 0 14px" }}>
            {[
              { k: "VA", n: liveRollup.VA, c: classOf("VA") },
              { k: "BVA", n: liveRollup.BVA, c: classOf("BVA") },
              { k: "NVA", n: liveRollup.NVA, c: classOf("NVA") },
              { k: "Unclassified", n: liveRollup.UNC, c: { color: "#6b7280", bg: "#f3f4f6" } },
            ].map((x) => (
              <div key={x.k} style={{ background: x.c.bg, border: `1px solid ${x.c.color}22`, borderRadius: 8, padding: "6px 12px", minWidth: 78 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: x.c.color, fontVariantNumeric: "tabular-nums" }}>{x.n}</div>
                <div style={{ fontSize: 11, color: x.c.color, fontWeight: 600 }}>{x.k}</div>
              </div>
            ))}
          </div>
          {liveRollup.NVA > 0 && (
            <div style={{ margin: "0 0 14px", fontSize: 12.5, color: "#b5341f", background: "#fbeae7", border: "1px solid #b5341f22", borderRadius: 8, padding: "8px 12px" }}>
              {liveRollup.NVA} non-value-adding step{liveRollup.NVA === 1 ? " is a" : "s are"} candidate{liveRollup.NVA === 1 ? "" : "s"} for elimination (FBPM Ch.6).
            </div>
          )}

          {/* Steps */}
          <div style={{ display: "grid", gap: 8 }}>
            {(data.tasks || []).map((t) => {
              const v = form[t.process_task_id] || {};
              return (
                <div key={t.process_task_id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", minWidth: 20 }}>{t.order}.</span>
                    <span style={{ fontWeight: 600, fontSize: 13.5, flex: 1, minWidth: 120 }}>
                      {t.task_name || "(step)"}
                      {t.task_code ? <span style={{ color: "#9ca3af", fontWeight: 400 }}> [{t.task_code}]</span> : null}
                      {t.is_child_process ? <span style={{ color: "#9ca3af", fontWeight: 400 }}> (sub-process)</span> : null}
                    </span>
                    {t.value_source ? (
                      <span
                        title={
                          t.value_source === "USER"
                            ? "Set by you — overrides the AI suggestion"
                            : "AI-suggested — the agent only fills steps left empty"
                        }
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: "2px 7px",
                          borderRadius: 999,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          border: `1px solid ${t.value_source === "USER" ? "#93c5fd" : "#fde68a"}`,
                          background: t.value_source === "USER" ? "#dbeafe" : "#fffbeb",
                          color: t.value_source === "USER" ? "#1d4ed8" : "#b45309",
                        }}
                      >
                        {t.value_source === "USER" ? "You" : "AI"}
                      </span>
                    ) : null}
                    {readOnly ? (
                      <Chip k={v.value_classification} />
                    ) : (
                      <div style={{ display: "flex", gap: 6 }}>
                        {CLASSES.map((c) => {
                          const on = v.value_classification === c.key;
                          return (
                            <button
                              key={c.key}
                              type="button"
                              onClick={() => pick(t.process_task_id, c.key)}
                              title={c.hint}
                              style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "4px 10px",
                                borderRadius: 999,
                                cursor: "pointer",
                                border: `1px solid ${on ? c.color : "#d1d5db"}`,
                                background: on ? c.color : "#fff",
                                color: on ? "#fff" : "#6b7280",
                              }}
                            >
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Rationale + BVA goal */}
                  {readOnly ? (
                    (v.value_rationale || v.bva_business_goal) && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                        {v.value_rationale && <div>{v.value_rationale}</div>}
                        {v.value_classification === "BVA" && v.bva_business_goal && (
                          <div style={{ marginTop: 2 }}><b>Business goal:</b> {v.bva_business_goal}</div>
                        )}
                      </div>
                    )
                  ) : (
                    v.value_classification && (
                      <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                        <input
                          style={inputStyle}
                          value={v.value_rationale || ""}
                          placeholder="Why? (rationale)"
                          onChange={(e) => setField(t.process_task_id, "value_rationale", e.target.value)}
                        />
                        {v.value_classification === "BVA" && (
                          <input
                            style={inputStyle}
                            value={v.bva_business_goal || ""}
                            placeholder="Business goal / requirement that justifies this step"
                            onChange={(e) => setField(t.process_task_id, "bva_business_goal", e.target.value)}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: "9px 20px", background: saving ? "#93c5fd" : "#2563eb", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: saving ? "default" : "pointer" }}
              >
                {saving ? "Saving…" : "Save classifications"}
              </button>
              {savedMsg && <span style={{ color: "#059669", fontSize: 13 }}>{savedMsg}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
